import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Plus, Check, AlertTriangle, Clock, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ControlesTab({ buildingId, token, onUpdate }) {
  const [controles, setControles] = useState([]);
  const [typesControles, setTypesControles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDoneModal, setShowDoneModal] = useState(null);
  const [form, setForm] = useState({});
  const [doneForm, setDoneForm] = useState({});

  const fetchControles = useCallback(async () => {
    try {
      const [ctrlRes, typesRes] = await Promise.all([
        fetch(`${API_URL}/api/carnet-entretien/controles/${buildingId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/carnet-entretien/types-controles`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (ctrlRes.ok) setControles(await ctrlRes.json());
      if (typesRes.ok) setTypesControles(await typesRes.json());
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [buildingId, token]);

  useEffect(() => { fetchControles(); }, [fetchControles]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/carnet-entretien/controles`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, building_id: buildingId })
      });
      if (response.ok) {
        toast.success('Contrôle ajouté');
        setShowAddModal(false); setForm({});
        fetchControles();
        if (onUpdate) onUpdate();
      }
    } catch (error) { toast.error('Erreur'); }
  };

  const handleMarkDone = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/carnet-entretien/controles/${showDoneModal}/effectue`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(doneForm)
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Contrôle enregistré. Prochaine échéance: ${new Date(result.prochaine_echeance).toLocaleDateString('fr-FR')}`);
        setShowDoneModal(null); setDoneForm({});
        fetchControles();
        if (onUpdate) onUpdate();
      }
    } catch (error) { toast.error('Erreur'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'en_retard': return <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium"><AlertTriangle className="h-3 w-3" />En retard</span>;
      case 'a_venir': return <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium"><Clock className="h-3 w-3" />À venir</span>;
      case 'ok': return <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium"><Check className="h-3 w-3" />À jour</span>;
      default: return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">Non planifié</span>;
    }
  };

  const openAddWithType = (type) => {
    setForm({
      type_controle: type.type,
      nom: type.nom,
      periodicite_mois: type.periodicite_mois,
      reference_reglementaire: type.reference,
      description: type.description
    });
    setShowAddModal(true);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div></div>;

  const enRetard = controles.filter(c => c.statut === 'en_retard');
  const aVenir = controles.filter(c => c.statut === 'a_venir');
  const aJour = controles.filter(c => c.statut === 'ok');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Contrôles obligatoires</h2>
          <p className="text-sm text-gray-500">{controles.length} contrôle(s) configuré(s)</p>
        </div>
        <button onClick={() => { setForm({}); setShowAddModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]">
          <Plus className="h-5 w-5" />Ajouter
        </button>
      </div>

      {/* Alerts */}
      {enRetard.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h3 className="font-semibold text-red-800">Contrôles en retard ({enRetard.length})</h3>
          </div>
          <div className="space-y-2">
            {enRetard.map(c => (
              <div key={c.id} className="flex items-center justify-between bg-white rounded-lg p-3">
                <div>
                  <p className="font-medium text-gray-900">{c.nom}</p>
                  <p className="text-xs text-red-600">Échéance dépassée: {formatDate(c.prochaine_echeance)}</p>
                </div>
                <button onClick={() => { setShowDoneModal(c.id); setDoneForm({ date_controle: new Date().toISOString().split('T')[0] }); }} className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600">Marquer effectué</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls List */}
      {controles.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <ClipboardList className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-center text-gray-500 mb-6">Aucun contrôle configuré</p>
          <h4 className="font-medium text-gray-900 mb-3">Ajouter un contrôle réglementaire :</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {typesControles.slice(0, 6).map(t => (
              <button key={t.type} onClick={() => openAddWithType(t)} className="p-3 text-left border border-gray-200 rounded-lg hover:border-[#00D3A8] hover:bg-[#00D3A8]/5">
                <p className="font-medium text-gray-900 text-sm">{t.nom}</p>
                <p className="text-xs text-gray-500">Tous les {t.periodicite_mois} mois</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contrôle</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Périodicité</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prochain</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {controles.map(c => (
                <tr key={c.id} className={`hover:bg-gray-50 ${c.statut === 'en_retard' ? 'bg-red-50/50' : ''}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{c.nom}</p>
                    {c.reference_reglementaire && <p className="text-xs text-gray-500">{c.reference_reglementaire}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.periodicite_mois} mois</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(c.dernier_controle)}</td>
                  <td className="px-4 py-3 text-sm font-medium">{formatDate(c.prochaine_echeance)}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(c.statut)}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => { setShowDoneModal(c.id); setDoneForm({ date_controle: new Date().toISOString().split('T')[0] }); }} className="px-3 py-1 bg-[#00D3A8] text-white text-sm rounded hover:bg-[#00B892]">
                      <Check className="h-4 w-4 inline mr-1" />Effectué
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">Ajouter un contrôle</h3>
              <button onClick={() => setShowAddModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleAdd} className="p-4 space-y-4">
              <div><label className="block text-sm font-medium mb-1">Type de contrôle</label>
                <select value={form.type_controle || ''} onChange={(e) => {
                  const type = typesControles.find(t => t.type === e.target.value);
                  if (type) setForm({ ...form, type_controle: type.type, nom: type.nom, periodicite_mois: type.periodicite_mois, reference_reglementaire: type.reference });
                  else setForm({ ...form, type_controle: e.target.value });
                }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Sélectionner</option>
                  {typesControles.map(t => <option key={t.type} value={t.type}>{t.nom}</option>)}
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Nom *</label><input type="text" value={form.nom || ''} onChange={(e) => setForm({...form, nom: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Périodicité (mois)</label><input type="number" value={form.periodicite_mois || 12} onChange={(e) => setForm({...form, periodicite_mois: parseInt(e.target.value) || 12})} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">Prochaine échéance</label><input type="date" value={form.prochaine_echeance || ''} onChange={(e) => setForm({...form, prochaine_echeance: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Référence réglementaire</label><input type="text" value={form.reference_reglementaire || ''} onChange={(e) => setForm({...form, reference_reglementaire: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mark Done Modal */}
      {showDoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">Enregistrer le contrôle</h3>
              <button onClick={() => setShowDoneModal(null)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleMarkDone} className="p-4 space-y-4">
              <div><label className="block text-sm font-medium mb-1">Date du contrôle *</label><input type="date" value={doneForm.date_controle || ''} onChange={(e) => setDoneForm({...doneForm, date_controle: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Organisme</label><input type="text" value={doneForm.organisme || ''} onChange={(e) => setDoneForm({...doneForm, organisme: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: APAVE, SOCOTEC" /></div>
              <div><label className="block text-sm font-medium mb-1">Résultat</label>
                <select value={doneForm.resultat || 'conforme'} onChange={(e) => setDoneForm({...doneForm, resultat: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  <option value="conforme">Conforme</option>
                  <option value="non_conforme">Non conforme</option>
                  <option value="avec_reserves">Avec réserves</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Observations</label><textarea value={doneForm.observations || ''} onChange={(e) => setDoneForm({...doneForm, observations: e.target.value})} rows="2" className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowDoneModal(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">Valider</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
